import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import './ContractWizard.css';
import { submitFormSession } from '../utils/formSessionApi';
import { trackFormWizardEvent, formWizardEventPayload } from '../utils/formWizardTelemetry';
import {
  fetchChildFormParents,
  fetchChildFormPrefill,
  fetchSessionPrefill,
  fetchFormCatalog,
  fetchSessionParents,
  generateChildForm,
} from '../utils/childFormApi';
import { firstIncompleteStepIndex } from '../utils/formSessionApi';
import {
  fieldRequired as stepFieldRequired,
  dropHiddenFields,
  hasValue,
  inputForField,
  inputTypeForField,
  labelForField,
  placeholderForField,
  stripPrintedItemNumber,
  firstIncompleteRequiredStepIndex,
  inferCancellingSide,
  normalizeRepresentedSides,
  nextVisibleStepIndex,
  optionsForField,
  isPropertyAddressStep,
  stepIsVisible,
  visibleFieldsForStep,
  progressStepIndexes,
} from '../utils/childFormWizardFields';
import PropertyAddressFields, {
  PROPERTY_ADDRESS_QUESTION,
  withAppendixALocation,
} from './PropertyAddressFields';
import {
  catalogGroupsFromPayload,
  childFormAppliesToPurchaseAgreement,
  ESCALATION_WIZARD_STEPS,
  OTHER_FORM_CATALOG,
} from '../utils/formTypeIds';
import { titleCaseFieldLabel } from '../utils/wizardFieldLabel';
import { isPropertyAddressComplete, isValidEmailAddress } from '../utils/wizardFieldValidation';
import {
  formatMissingEmailsReason,
  WIZARD_PRIMARY_BUTTON_CLASS,
} from '../utils/wizardContinue';
import WizardProgress from './WizardProgress';
import WizardStepReady from './WizardStepReady';
import WizardContinueHint from './WizardContinueHint';

const PARENT_KIND_COLORS = {
  contract: '#7fb069',
  listing: '#5a8f4a',
  buyer_agreement: '#D4A574',
};

function encodeParentChoice(kind, id) {
  if (!kind || !id) return '';
  return `${kind}:${id}`;
}

function decodeParentChoice(value) {
  const raw = String(value || '');
  const sep = raw.indexOf(':');
  if (sep < 0) return { kind: '', id: '' };
  return { kind: raw.slice(0, sep), id: raw.slice(sep + 1) };
}

function combinedParentOptions(parents) {
  const rows = [];
  (parents.contracts || []).forEach((row) => {
    rows.push({ ...row, kind: row.kind || 'contract', field: 'parent_contract_id' });
  });
  (parents.listings || []).forEach((row) => {
    rows.push({ ...row, kind: row.kind || 'listing', field: 'parent_listing_id' });
  });
  (parents.buyer_agreements || []).forEach((row) => {
    rows.push({ ...row, kind: row.kind || 'buyer_agreement', field: 'parent_buyer_agreement_id' });
  });
  return rows.sort((left, right) => (
    String(left.label || '').localeCompare(String(right.label || ''), undefined, { sensitivity: 'base' })
  ));
}

function selectedParentValue(values) {
  if (values.parent_contract_id) return encodeParentChoice('contract', values.parent_contract_id);
  if (values.parent_listing_id) return encodeParentChoice('listing', values.parent_listing_id);
  if (values.parent_buyer_agreement_id) {
    return encodeParentChoice('buyer_agreement', values.parent_buyer_agreement_id);
  }
  return '';
}

function parentIdentity(values) {
  return [
    values.parent_contract_id || '',
    values.parent_listing_id || '',
    values.parent_buyer_agreement_id || '',
  ].join('|');
}

const PARENT_KIND_LABELS = {
  contract: 'Contract',
  listing: 'Listing',
  buyer_agreement: 'Buyer agreement',
};

function ParentKindMark({ kind }) {
  const label = PARENT_KIND_LABELS[kind];
  if (!label) return null;
  return (
    <span className="wizard-parent-kind" style={{ color: PARENT_KIND_COLORS[kind] }}>
      {label}
    </span>
  );
}

function parentPickerCopy(fields) {
  const list = fields || [];
  if (list.includes('parent_buyer_agreement_id') && !list.includes('parent_listing_id')) {
    return {
      prompt: 'Which buyer is this form for?',
      fieldLabel: 'Buyer client',
      placeholder: 'Select a buyer',
      ariaLabel: 'Buyer client',
    };
  }
  return {
    prompt: 'Which transaction is this form for?',
    fieldLabel: 'Property Address',
    placeholder: 'Select a property',
    ariaLabel: 'Property Address',
  };
}

function ParentPropertyPicker({ value, options, onChange, placeholder, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selected = options.find((row) => encodeParentChoice(row.kind, row.id) === value);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="wizard-parent-picker" ref={rootRef}>
      <button
        type="button"
        className="wizard-parent-picker-button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        {selected ? (
          <>
            <ParentKindMark kind={selected.kind} />
            <span className="wizard-parent-address">{selected.label}</span>
          </>
        ) : (
          <span className="wizard-parent-placeholder">{placeholder}</span>
        )}
      </button>
      {open && (
        <div className="wizard-parent-menu" role="listbox" aria-label={`${ariaLabel} options`}>
          <button
            type="button"
            className="wizard-parent-option"
            role="option"
            aria-selected={!value}
            data-parent-value=""
            onClick={() => {
              onChange('');
              setOpen(false);
            }}
          >
            <span className="wizard-parent-placeholder">{placeholder}</span>
          </button>
          {options.map((row) => {
            const choice = encodeParentChoice(row.kind, row.id);
            return (
              <button
                key={`${row.kind}-${row.id}`}
                type="button"
                className="wizard-parent-option"
                role="option"
                aria-selected={choice === value}
                data-parent-value={choice}
                onClick={() => {
                  onChange(choice);
                  setOpen(false);
                }}
              >
                <ParentKindMark kind={row.kind} />
                <span className="wizard-parent-address">{row.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const FIELD_LABELS = {
  addendum_body: 'Addendum Terms',
  purchase_price: 'Purchase Price',
  acceptance_deadline: 'Acceptance Deadline',
  other_terms: 'Other Terms',
  escalation_increment: 'Escalation Amount',
  seller_name: 'Seller Names',
  max_purchase_price: 'Maximum Purchase Price',
  financing_procedure: 'Financing Procedure',
  financing_loan_percent: 'Loan Percent of the New Purchase Price',
  financing_loan_cap: 'Maximum Loan Amount ($)',
  financing_other: 'Other Financing Terms',
  competing_offer_closing_days: 'Competing-Offer Closing Days',
  acceptance_method: 'Acceptance Method',
  acceptance_other: 'Other Acceptance Terms',
  linked_contract_id: 'Which Purchase Agreement',
  listing_price: 'Listing Price',
  expiration_date: 'Expiration Date',
  buyers_agent_commission_percent: 'Commission Percent',
  buyers_agent_commission_amount: 'Commission Amount',
};

const COUNTER_FALLBACK_STEPS = [
  { step_id: 'parent', fields: ['parent_contract_id', 'parent_listing_id', 'parent_buyer_agreement_id'] },
  { step_id: 'purchase_price', fields: ['purchase_price'], input: 'number' },
  { step_id: 'acceptance_deadline', fields: ['acceptance_deadline'], input: 'datetime' },
  { step_id: 'other_terms', fields: ['other_terms'], input: 'textarea', optional: true },
];

const FALLBACK_WIZARD_STEPS = {
  addendum: [
    { step_id: 'parent', fields: ['parent_contract_id'] },
    { step_id: 'addendum_body', fields: ['addendum_body'], input: 'textarea' },
  ],
  escalation_addendum: ESCALATION_WIZARD_STEPS,
  sellers_counteroffer: COUNTER_FALLBACK_STEPS,
  buyers_counteroffer: COUNTER_FALLBACK_STEPS,
  disclosure_of_brokerage_services_seller: [
    { step_id: 'parent', fields: ['parent_listing_id', 'parent_contract_id'] },
  ],
  disclosure_of_brokerage_services_buyer: [
    { step_id: 'parent', fields: ['parent_buyer_agreement_id', 'parent_contract_id'] },
  ],
};

function defaultStepsFor(formType) {
  if (FALLBACK_WIZARD_STEPS[formType]) return FALLBACK_WIZARD_STEPS[formType];
  return [
    { step_id: 'parent', fields: ['parent_contract_id', 'parent_listing_id', 'parent_buyer_agreement_id'] },
  ];
}

function parentPicked(values) {
  return hasValue(values.parent_contract_id)
    || hasValue(values.parent_listing_id)
    || hasValue(values.parent_buyer_agreement_id);
}

export function wizardUserFacingError(err, fallback = 'Could not generate the form.') {
  const code = String(err?.data?.error || '').trim();
  const message = String(err?.message || err?.data?.message || '').trim();
  const internal = code === 'unbound_identity'
    || err?.status >= 500
    || /token carries no bindable/i.test(message)
    || /unbound_identity/i.test(message)
    || /keyflo has been notified/i.test(message);
  if (internal) {
    return '';
  }
  return message || fallback;
}

const PRINCIPAL_EMAIL_ROLES = {
  seller: ['seller', 'co_seller'],
  buyer: ['buyer', 'co_buyer'],
};

const AGENT_EMAIL_ROLES = {
  seller: [
    'sellers_agent', 'co_sellers_agent', 'listing_agent', 'seller_agent',
    'co_listing_agent', 'co_seller_agent',
  ],
  buyer: [
    'buyers_agent', 'co_buyers_agent', 'buyer_agent', 'co_buyer_agent',
  ],
};

const CONTRACT_AGENT_FIELDS = {
  seller: [
    { nameKey: 'sellers_agent_name', emailKey: 'sellers_agent_email', role: 'listing_agent' },
    { nameKey: 'listing_agent_name', emailKey: 'listing_agent_email', role: 'listing_agent' },
  ],
  buyer: [
    { nameKey: 'buyers_agent_name', emailKey: 'buyers_agent_email', role: 'buyer_agent' },
    { nameKey: 'buyer_agent_name', emailKey: 'buyer_agent_email', role: 'buyer_agent' },
  ],
};

function partyRoleKey(role) {
  return String(role || '').trim().toLowerCase().replace(/['’]/g, '').replace(/[-\s]+/g, '_');
}

const ENTITY_NAME_MARKERS = [
  ' llc', ' l.l.c', ' inc', ' ltd', ' lp', ' l.p', ' trust', ' corp', ' pc', ' p.c',
];

function looksLikeEntityName(text) {
  const lowered = ` ${String(text || '').toLowerCase()} `;
  return ENTITY_NAME_MARKERS.some((marker) => lowered.includes(marker));
}

function splitPrincipalNames(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || '').trim()).filter(Boolean);
  }
  const text = String(value || '').trim();
  if (!text) return [];
  if (text.includes('|')) {
    return text.split('|').map((part) => part.trim()).filter(Boolean);
  }
  const parts = text.includes(' & ')
    ? text.split(' & ').map((part) => part.trim()).filter(Boolean)
    : [text];
  if (parts.length === 1) return parts;
  if (looksLikeEntityName(text)) return [text];
  return parts;
}

function namesOverlap(left, right) {
  const leftTokens = new Set(String(left || '').toLowerCase().split(/\s+/).filter(Boolean));
  const rightTokens = new Set(String(right || '').toLowerCase().split(/\s+/).filter(Boolean));
  if (!leftTokens.size || !rightTokens.size) return false;
  return [...leftTokens].every((token) => rightTokens.has(token))
    || [...rightTokens].every((token) => leftTokens.has(token));
}

function emailForPrincipalName(name, contacts) {
  const labeled = (contacts || []).filter((party) => String(party.name || '').trim());
  const exact = labeled.filter((party) => (
    String(party.name || '').trim() === name && String(party.email || '').trim()
  ));
  if (exact.length) {
    const emails = new Set(exact.map((party) => String(party.email).trim().toLowerCase()));
    return emails.size === 1 ? String(exact[0].email).trim() : '';
  }
  const hits = labeled.filter((party) => (
    String(party.email || '').trim() && namesOverlap(name, party.name)
  ));
  return hits.length === 1 ? String(hits[0].email).trim() : '';
}

function representedPrincipals(values) {
  const sides = wizardEmailSides(values);
  const people = [];
  const addSide = (side, namesKey, nameKey, roles) => {
    if (!sides.includes(side)) return;
    const contacts = (values.parties || []).filter((party) => (
      roles.includes(partyRoleKey(party.role))
    ));
    const names = splitPrincipalNames(values[namesKey]).length
      ? splitPrincipalNames(values[namesKey])
      : splitPrincipalNames(values[nameKey]);
    if (names.length) {
      names.forEach((name) => {
        people.push({
          role: side,
          name,
          email: emailForPrincipalName(name, contacts),
        });
      });
      return;
    }
    contacts.forEach((party) => {
      const name = String(party.name || '').trim();
      if (!name) return;
      people.push({
        role: partyRoleKey(party.role),
        name,
        email: String(party.email || '').trim(),
      });
    });
  };
  addSide('seller', 'seller_names', 'seller_name', PRINCIPAL_EMAIL_ROLES.seller);
  addSide('buyer', 'buyer_names', 'buyer_name', PRINCIPAL_EMAIL_ROLES.buyer);
  return people;
}

function wizardEmailSides(values) {
  if (String(values?.completion_level || '').toLowerCase() === 'header') {
    return [];
  }
  const declared = Array.isArray(values?.represented_sides)
    ? values.represented_sides.filter((side) => side === 'buyer' || side === 'seller')
    : null;
  if (declared && declared.length) return declared;
  if (values?.parent_listing_id && !values?.parent_contract_id) return ['seller'];
  if (values?.parent_buyer_agreement_id && !values?.parent_contract_id) return ['buyer'];
  return inferredEmailSides(values);
}

function inferredEmailSides(values) {
  const parties = values?.parties || [];
  const seller = Boolean(
    splitPrincipalNames(values?.seller_names).length
    || splitPrincipalNames(values?.seller_name).length
    || parties.some((party) => PRINCIPAL_EMAIL_ROLES.seller.includes(partyRoleKey(party.role)))
  );
  const buyer = Boolean(
    splitPrincipalNames(values?.buyer_names).length
    || splitPrincipalNames(values?.buyer_name).length
    || parties.some((party) => PRINCIPAL_EMAIL_ROLES.buyer.includes(partyRoleKey(party.role)))
  );
  if (seller && buyer) return [];
  if (seller) return ['seller'];
  if (buyer) return ['buyer'];
  return [];
}

function missingPrincipalEmails(values) {
  return representedPrincipals(values).filter((party) => !isValidEmailAddress(party.email));
}

function representedAgents(values) {
  if (!values.agent_is_signer) return [];
  const sides = wizardEmailSides(values);
  const people = [];
  const addSide = (side, roles) => {
    if (!sides.includes(side)) return;
    (values.parties || []).forEach((party) => {
      if (!roles.includes(partyRoleKey(party.role))) return;
      const name = String(party.name || '').trim();
      if (!name) return;
      people.push({
        role: party.role,
        name,
        email: String(party.email || '').trim(),
      });
    });
  };
  addSide('seller', AGENT_EMAIL_ROLES.seller);
  addSide('buyer', AGENT_EMAIL_ROLES.buyer);
  const addContractField = (side) => {
    if (!sides.includes(side)) return;
    if (people.some((person) => AGENT_EMAIL_ROLES[side].includes(partyRoleKey(person.role)))) {
      return;
    }
    for (const field of CONTRACT_AGENT_FIELDS[side]) {
      const name = String(values?.[field.nameKey] || '').trim();
      if (!name) continue;
      people.push({
        role: field.role,
        name,
        email: String(values?.[field.emailKey] || '').trim(),
      });
      return;
    }
  };
  addContractField('seller');
  addContractField('buyer');
  return people;
}

function missingSignerEmails(values) {
  return [
    ...missingPrincipalEmails(values),
    ...representedAgents(values).filter((party) => !isValidEmailAddress(party.email)),
  ];
}

function signerEmailLabel(person) {
  const role = String(person.role || '').replace(/_/g, ' ').trim();
  if (!role || role === 'seller' || role === 'buyer') return person.name;
  return `${person.name} (${role})`;
}

function updatePartyEmail(parties, name, role, email) {
  let filled = false;
  const next = (parties || []).map((party) => {
    if (filled) return party;
    if (String(party.name || '').trim() !== name) return party;
    if (partyRoleKey(party.role) !== partyRoleKey(role)) return party;
    filled = true;
    return { ...party, email };
  });
  if (!filled) next.push({ role, name, email });
  return next;
}

function cardStepsForFormType(formType, groups) {
  for (const group of groups) {
    const match = (group.forms || []).find((form) => form.formType === formType);
    if (match?.wizardSteps?.length) return match.wizardSteps;
  }
  return [];
}

function wizardInputFields(stepList) {
  const fields = [];
  (stepList || []).forEach((item) => {
    (item.fields || []).forEach((field) => {
      if (field.startsWith('parent_') || field === 'linked_contract_id') return;
      if (!fields.includes(field)) fields.push(field);
    });
  });
  return fields;
}

function keepDirtyWizardFields(prev, dirty) {
  const kept = {};
  dirty.forEach((field) => {
    if (field.startsWith('parent_') || field === 'linked_contract_id') return;
    if (hasValue(prev[field])) kept[field] = prev[field];
  });
  return kept;
}

const ChildFormWizard = ({
  isOpen = true,
  mode = 'modal',
  onClose,
  onSuccess,
  initialValues = {},
  wizardSteps = [],
  optionalWizardFields = [],
  stillNeededLabels = {},
  formSessionToken,
  userEmail,
  formType,
  displayName,
  parentLocked = false,
  allowEarlySubmitAfter = '',
  initialSuccess = false,
  initialEntityId = null,
  initialError = '',
  onBackToCatalog,
}) => {
  const [values, setValues] = useState({ ...initialValues });
  const [parents, setParents] = useState({ contracts: [], listings: [], buyer_agreements: [] });
  const [resolvedSteps, setResolvedSteps] = useState(wizardSteps);
  const [step, setStep] = useState(0);
  const [error, setError] = useState(initialError || '');
  const [parentsLoaded, setParentsLoaded] = useState(Boolean(parentLocked));
  const [prefillLoaded, setPrefillLoaded] = useState(!parentPicked(initialValues));
  const [prefillFor, setPrefillFor] = useState(() => (
    parentLocked || parentPicked(initialValues) ? parentIdentity(initialValues) : ''
  ));
  const [committingParent, setCommittingParent] = useState(false);
  const [needPrincipalEmails, setNeedPrincipalEmails] = useState(
    () => missingSignerEmails(initialValues).length > 0,
  );
  const { getAccessTokenSilently, getIdTokenClaims, user: auth0User } = useAuth0();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(
    initialSuccess
      ? { entity_id: initialEntityId, entity_type: initialValues.entity_type }
      : null
  );
  const stepIdRef = useRef(null);
  const advanceTimerRef = useRef(null);
  const dirtyFieldsRef = useRef(new Set());
  const afterPrefillRef = useRef(null);
  const submitRef = useRef(null);

  const linkedContracts = useMemo(
    () => values.linked_contracts || initialValues.linked_contracts || [],
    [values.linked_contracts, initialValues.linked_contracts],
  );

  useEffect(() => {
    if (missingSignerEmails(values).length) {
      setNeedPrincipalEmails(true);
    }
  }, [values]);

  const steps = useMemo(() => {
    const raw = resolvedSteps.length ? resolvedSteps : defaultStepsFor(formType);
    let next = parentLocked ? raw.filter((item) => item.step_id !== 'parent') : raw;
    if (linkedContracts.length > 1 && childFormAppliesToPurchaseAgreement(formType)) {
      const hasLinked = next.some((item) => (item.fields || []).includes('linked_contract_id'));
      if (!hasLinked) {
        const parentIdx = next.findIndex((item) => item.step_id === 'parent');
        const insertAt = parentIdx >= 0 ? parentIdx + 1 : 0;
        next = [
          ...next.slice(0, insertAt),
          { step_id: 'linked_contract', fields: ['linked_contract_id'] },
          ...next.slice(insertAt),
        ];
      }
    }
    if (
      (needPrincipalEmails || missingSignerEmails(values).length)
      && !next.some((item) => item.step_id === 'principal_emails')
    ) {
      next = [...next, { step_id: 'principal_emails', fields: [] }];
    }
    return next;
  }, [resolvedSteps, parentLocked, formType, linkedContracts, values, needPrincipalEmails]);

  const parentSeed = [
    initialValues.parent_contract_id || '',
    initialValues.parent_listing_id || '',
    initialValues.parent_buyer_agreement_id || '',
    formType,
    inferCancellingSide(initialValues) || '',
    normalizeRepresentedSides(initialValues.represented_sides).join(','),
  ].join('|');

  useEffect(() => {
    if (formSessionToken) return undefined;
    const email = userEmail || auth0User?.email;
    if (!email || !formType) return undefined;
    Promise.resolve(trackFormWizardEvent(getAccessTokenSilently, formWizardEventPayload({
      phase: 'started',
      formType,
      userEmail: email,
      userName: auth0User?.name || auth0User?.nickname || '',
    }))).catch(() => {});
    return undefined;
  }, [formType, formSessionToken]);

  useEffect(() => {
    const inferredSide = inferCancellingSide(initialValues);
    const seeded = inferredSide
      ? { ...initialValues, cancelling_side: inferredSide }
      : { ...initialValues };
    const idx = firstIncompleteStepIndex(steps, seeded, optionalWizardFields);
    dirtyFieldsRef.current = new Set(
      wizardInputFields(steps).filter((field) => hasValue(seeded[field])),
    );
    setValues(seeded);
    setResolvedSteps(wizardSteps);
    setNeedPrincipalEmails(missingSignerEmails(initialValues).length > 0);
    setPrefillFor(parentLocked || parentPicked(initialValues) ? parentIdentity(initialValues) : '');
    setCommittingParent(false);
    afterPrefillRef.current = null;
    setStep(idx);
    stepIdRef.current = steps[idx]?.step_id || null;
    // Re-seed only when the parent deal or form type changes, not on object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentSeed]);

  useEffect(() => {
    if (wizardSteps.length) {
      setResolvedSteps(wizardSteps);
      return undefined;
    }
    let cancelled = false;
    fetchFormCatalog({ getAccessTokenSilently, getIdTokenClaims }).then((payload) => {
      const groups = catalogGroupsFromPayload(payload);
      const catalog = groups.length ? groups : OTHER_FORM_CATALOG;
      const next = cardStepsForFormType(formType, catalog);
      if (!cancelled && next.length) setResolvedSteps(next);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [formType, wizardSteps, getAccessTokenSilently]);

  useEffect(() => {
    if (parentLocked) {
      setParentsLoaded(true);
      return undefined;
    }
    let cancelled = false;
    const load = formSessionToken
      ? fetchSessionParents(formSessionToken)
      : (userEmail && formType
        ? fetchChildFormParents(formType, userEmail, { getAccessTokenSilently, getIdTokenClaims })
        : null);
    if (!load) {
      setParentsLoaded(true);
      return undefined;
    }
    load.then((data) => {
      if (!cancelled) setParents(data);
    }).catch(() => {}).finally(() => {
      if (!cancelled) setParentsLoaded(true);
    });
    return () => { cancelled = true; };
    // Auth0's getAccessTokenSilently is a new function each render; do not
    // refetch (or hide the step) when only that identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formType, parentLocked, userEmail, formSessionToken]);

  useEffect(() => {
    if (!formType || !prefillFor || (!formSessionToken && !userEmail)) {
      setPrefillLoaded(true);
      return undefined;
    }
    const [contractId, listingId, buyerAgreementId] = prefillFor.split('|');
    let cancelled = false;
    setPrefillLoaded(false);
    const load = formSessionToken
      ? fetchSessionPrefill(formSessionToken, {
        formType,
        contractId: contractId || '',
        listingId: listingId || '',
        buyerAgreementId: buyerAgreementId || '',
      })
      : fetchChildFormPrefill({
        formType,
        userEmail,
        contractId: contractId || '',
        listingId: listingId || '',
        buyerAgreementId: buyerAgreementId || '',
        getAccessTokenSilently,
        getIdTokenClaims,
      });
    load.then((data) => {
      if (cancelled) return;
      const prefill = data.prefill || {};
      setNeedPrincipalEmails(missingSignerEmails(prefill).length > 0);
      setValues((prev) => {
        const merged = {
          ...prefill,
          ...keepDirtyWizardFields(prev, dirtyFieldsRef.current),
          parent_contract_id: prev.parent_contract_id,
          parent_listing_id: prev.parent_listing_id,
          parent_buyer_agreement_id: prev.parent_buyer_agreement_id,
          linked_contracts: prefill.linked_contracts || [],
          linked_contract_ambiguous: prefill.linked_contract_ambiguous,
          linked_contract_id: prefill.linked_contract_id || '',
        };
        const inferred = inferCancellingSide(merged);
        if (inferred) merged.cancelling_side = inferred;
        return merged;
      });
      if (!wizardSteps.length && data.wizard_steps?.length && !resolvedSteps.length) {
        setResolvedSteps(data.wizard_steps);
      }
    }).catch((err) => {
      if (cancelled) return;
      const message = wizardUserFacingError(err, 'Could not load details for that transaction.');
      if (message) setError(message);
      afterPrefillRef.current = null;
      setCommittingParent(false);
      setPrefillFor('');
    }).finally(() => {
      if (!cancelled) setPrefillLoaded(true);
    });
    return () => { cancelled = true; };
    // Prefill only after Continue / a locked parent — not when the picker changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formType, userEmail, formSessionToken, prefillFor]);

  const stepsKey = steps.map((item) => item.step_id).join('|');
  useEffect(() => {
    if (committingParent) return;
    if (!parentLocked && stepIdRef.current === 'parent') return;
    const linkedIdx = steps.findIndex((item) => item.step_id === 'linked_contract');
    const needsLinked = linkedIdx >= 0 && !hasValue(values.linked_contract_id)
      && (parentLocked || parentPicked(values));
    if (needsLinked) {
      stepIdRef.current = 'linked_contract';
      setStep(linkedIdx);
      return;
    }
    const emailIdx = steps.findIndex((item) => item.step_id === 'principal_emails');
    const emailGaps = missingSignerEmails(values);
    if (
      emailIdx >= 0
      && emailGaps.length
      && stepIdRef.current !== 'principal_emails'
      && stepIdRef.current !== 'parent'
    ) {
      const otherHole = firstIncompleteRequiredStepIndex(
        steps.filter((item) => item.step_id !== 'principal_emails'),
        values,
        optionalWizardFields,
      );
      if (otherHole < 0) {
        stepIdRef.current = 'principal_emails';
        setStep(emailIdx);
        return;
      }
    }
    const byId = stepIdRef.current
      ? steps.findIndex((item) => item.step_id === stepIdRef.current)
      : -1;
    if (byId >= 0) setStep(byId);
    // Remap only after a committed parent / step-list change, not while picking.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepsKey, values.linked_contract_id, parentLocked, prefillFor, committingParent]);

  useEffect(() => {
    if (!committingParent || !prefillLoaded) return;
    const action = afterPrefillRef.current;
    afterPrefillRef.current = null;
    setCommittingParent(false);
    if (!action) return;
    const parentIdx = steps.findIndex((item) => item.step_id === 'parent');
    const nextIdx = nextVisibleStepIndex(steps, parentIdx >= 0 ? parentIdx : -1, values, 1);
    if (nextIdx >= 0 && nextIdx < steps.length) {
      stepIdRef.current = steps[nextIdx].step_id;
      setStep(nextIdx);
      return;
    }
    if (action === 'generate') {
      submitRef.current?.('full');
    }
    // After Continue on the parent step, extra questions (emails, linked PA) may appear.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [committingParent, prefillLoaded, stepsKey]);

  useEffect(() => {
    if (!prefillLoaded || committingParent) return;
    const currentStep = steps[step];
    if (stepIsVisible(currentStep, values)) return;
    const nextIdx = nextVisibleStepIndex(steps, Math.max(step - 1, -1), values, 1);
    if (nextIdx >= 0 && nextIdx < steps.length && nextIdx !== step) {
      stepIdRef.current = steps[nextIdx].step_id;
      setStep(nextIdx);
    }
    // Prefill can hide the cancelling-side question once the deal side is known.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillLoaded, committingParent, values.cancelling_side, values.represented_sides, stepsKey]);

  useEffect(() => () => {
    if (advanceTimerRef.current) {
      window.clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
  }, []);

  if (!isOpen) return null;

  const current = steps[step] || { fields: [] };
  const isParentStep = current.step_id === 'parent';
  const pickerCopy = parentPickerCopy(current.fields);
  const isPrincipalEmailStep = current.step_id === 'principal_emails';
  const principalEmailPeople = [
    ...representedPrincipals(values),
    ...representedAgents(values),
  ];
  const principalEmailGaps = missingSignerEmails(values);
  const labels = {
    ...FIELD_LABELS,
    ...stillNeededLabels,
    ...(initialValues.stillNeededLabels || {}),
  };
  const optionalFields = [
    ...optionalWizardFields,
    ...(initialValues.optionalWizardFields || []),
  ];
  const incompleteRequired = firstIncompleteRequiredStepIndex(
    steps, values, optionalFields, principalEmailGaps,
  );
  const last = nextVisibleStepIndex(steps, step, values, 1) >= steps.length;
  const showGenerate = last && (incompleteRequired < 0 || incompleteRequired === step);

  const setField = (field, value) => {
    dirtyFieldsRef.current.add(field);
    setValues((prev) => dropHiddenFields(steps, { ...prev, [field]: value }));
  };

  const setAddressField = (field, value) => {
    dirtyFieldsRef.current.add(field);
    setValues((prev) => withAppendixALocation(
      dropHiddenFields(steps, { ...prev, [field]: value }),
    ));
  };

  const clearAdvanceTimer = () => {
    if (advanceTimerRef.current) {
      window.clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
  };

  const goToVisibleStep = (fromIndex, nextValues, direction) => {
    clearAdvanceTimer();
    const nextIdx = nextVisibleStepIndex(steps, fromIndex, nextValues, direction);
    if (nextIdx < 0 || nextIdx >= steps.length) return false;
    stepIdRef.current = steps[nextIdx].step_id;
    setStep(nextIdx);
    return true;
  };

  const chooseAndAdvance = (field, value) => {
    dirtyFieldsRef.current.add(field);
    const from = step;
    const fromId = current.step_id;
    setValues((prev) => {
      const nextValues = dropHiddenFields(steps, { ...prev, [field]: value });
      const currentStep = steps[from] || current;
      const remaining = visibleFieldsForStep(currentStep, nextValues).filter((name) => (
        !name.startsWith('parent_')
        && stepFieldRequired(name, currentStep, nextValues, optionalFields)
        && !hasValue(nextValues[name])
      ));
      if (remaining.length) {
        clearAdvanceTimer();
        return nextValues;
      }
      clearAdvanceTimer();
      advanceTimerRef.current = window.setTimeout(() => {
        advanceTimerRef.current = null;
        if (stepIdRef.current !== fromId) return;
        goToVisibleStep(from, nextValues, 1);
      }, 180);
      return nextValues;
    });
  };

  const fieldRequired = (field) => stepFieldRequired(field, current, values, optionalFields);
  const visibleFields = visibleFieldsForStep(current, values).filter((field) => (
    !field.startsWith('parent_')
  ));
  const declaredChoiceFields = (current.fields || []).filter((field) => !field.startsWith('parent_'));
  const progressIndexes = progressStepIndexes(steps, values);
  const progressPosition = Math.max(progressIndexes.indexOf(step), 0);
  const singleChoiceField = declaredChoiceFields.length === 1
    && ['radio', 'checkbox'].includes(inputForField(declaredChoiceFields[0], current));
  const promptOnlyInput = Boolean(current.prompt)
    && !singleChoiceField
    && visibleFields.length === 1
    && ['text', 'textarea', 'number', 'date', 'datetime'].includes(
      inputForField(visibleFields[0], current),
    );
  const questionLabel = stripPrintedItemNumber(
    isParentStep
      ? pickerCopy.prompt
      : (current.prompt || (visibleFields[0]
        ? titleCaseFieldLabel(labelForField(visibleFields[0], current, labels))
        : '')),
  );

  const isAddressStep = isPropertyAddressStep(current);
  const canContinue = () => {
    if (isParentStep) return parentPicked(values);
    if (isPrincipalEmailStep) return principalEmailGaps.length === 0;
    if (isAddressStep) return isPropertyAddressComplete(values);
    return (current.fields || []).every((field) => (
      !fieldRequired(field) || hasValue(values[field])
    ));
  };

  const continueBlockedReason = () => {
    if (!isPrincipalEmailStep) return '';
    return formatMissingEmailsReason(principalEmailGaps.map((person) => person.name));
  };

  const submit = async (completionLevel = 'full') => {
    if (completionLevel !== 'header' && (incompleteRequired >= 0 || principalEmailGaps.length)) {
      const emailIdx = steps.findIndex((item) => item.step_id === 'principal_emails');
      const target = incompleteRequired >= 0 ? incompleteRequired : emailIdx;
      if (target >= 0) {
        stepIdRef.current = steps[target]?.step_id || null;
        setStep(target);
        setError('');
        return;
      }
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = completionLevel === 'header'
        ? {
          form_type: formType,
          user_email: userEmail,
          completion_level: 'header',
          parent_listing_id: values.parent_listing_id || '',
          parent_contract_id: values.parent_contract_id || '',
          parent_buyer_agreement_id: values.parent_buyer_agreement_id || '',
          year_built: values.year_built || '',
          property_address: values.property_address || '',
          linked_contract_id: values.linked_contract_id || '',
        }
        : {
          ...dropHiddenFields(steps, values),
          form_type: formType,
          user_email: userEmail,
          completion_level: 'full',
        };
      const result = formSessionToken
        ? await submitFormSession(formSessionToken, payload)
        : await generateChildForm(payload, { getAccessTokenSilently, getIdTokenClaims });
      setDone(result);
      if (onSuccess) onSuccess(result);
      const email = userEmail || auth0User?.email;
      if (!formSessionToken && email) {
        Promise.resolve(trackFormWizardEvent(getAccessTokenSilently, formWizardEventPayload({
          phase: 'completed',
          formType,
          userEmail: email,
          userName: auth0User?.name || auth0User?.nickname || '',
          metadata: {
            entity_id: result?.entity_id || result?.id,
            entity_type: result?.entity_type,
            form_type: formType,
          },
        }))).catch(() => {});
      }
    } catch (err) {
      const message = wizardUserFacingError(err, 'Could not generate the form.');
      if (message) setError(message);
    } finally {
      setSubmitting(false);
    }
  };
  submitRef.current = submit;

  const commitParentThen = (action) => {
    const key = parentIdentity(values);
    if (!key) return;
    const stillNeedEmails = missingSignerEmails(values).length > 0;
    if (key === prefillFor && prefillLoaded) {
      setNeedPrincipalEmails(stillNeedEmails);
      if (stillNeedEmails) {
        afterPrefillRef.current = action;
        setCommittingParent(true);
        return;
      }
      const parentIdx = steps.findIndex((item) => item.step_id === 'parent');
      const nextIdx = parentIdx + 1;
      if (nextIdx > 0 && nextIdx < steps.length) {
        stepIdRef.current = steps[nextIdx].step_id;
        setStep(nextIdx);
        return;
      }
      if (action === 'generate') submit('full');
      return;
    }
    afterPrefillRef.current = action;
    setPrefillLoaded(false);
    setCommittingParent(true);
    setPrefillFor(key);
  };

  const body = (
    <div className="wizard-container">
      <div className="wizard-header">
        <h2>{displayName || 'Generate form'}</h2>
        <button className="wizard-close" onClick={() => onClose && onClose(done)}>×</button>
      </div>
      {!done && progressIndexes.length > 0 && (
        <WizardProgress current={progressPosition + 1} total={progressIndexes.length} />
      )}
      <div className="wizard-content" style={{ padding: '24px' }}>
        {done ? (
          <>
            <div className="result-message success">
              ✅ {displayName || 'Form'} created successfully!
            </div>
            {done.summary?.completion_level === 'header' && (
              <p className="wizard-step-prompt">
                This is a starter PDF with the header filled in. The property-condition
                questions are still blank for you to finish with the seller. Generate a
                complete copy before sending for e-sign.
              </p>
            )}
            <div className="result-details">
              {(done.summary?.property_address || values.property_address) && (
                <p>
                  <strong>Property:</strong>
                  {' '}
                  {done.summary?.property_address || values.property_address}
                </p>
              )}
              {done.summary?.buyer_name && (
                <p><strong>Buyer:</strong> {done.summary.buyer_name}</p>
              )}
              {done.summary?.seller_name && (
                <p><strong>Seller:</strong> {done.summary.seller_name}</p>
              )}
            </div>
            <div className="ba-wizard-success-actions">
              <button className="wizard-button primary" type="button" onClick={() => onClose && onClose(done)}>
                View form
              </button>
            </div>
          </>
        ) : (
          <WizardStepReady ready={parentsLoaded && !committingParent && (isParentStep || prefillLoaded)}>
          <>
            {error && <p className="form-magic-error">{error}</p>}
            {isParentStep && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <p style={{ margin: 0 }}>{pickerCopy.prompt}</p>
                <label className="wizard-field" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <span className="wizard-field-label">{pickerCopy.fieldLabel}</span>
                  <ParentPropertyPicker
                    value={selectedParentValue(values)}
                    options={combinedParentOptions(parents)}
                    placeholder={pickerCopy.placeholder}
                    ariaLabel={pickerCopy.ariaLabel}
                    onChange={(choice) => {
                      const { kind, id } = decodeParentChoice(choice);
                      const nextParent = {
                        parent_contract_id: kind === 'contract' ? id : '',
                        parent_listing_id: kind === 'listing' ? id : '',
                        parent_buyer_agreement_id: kind === 'buyer_agreement' ? id : '',
                      };
                      if (parentIdentity(nextParent) === prefillFor) {
                        setNeedPrincipalEmails(missingSignerEmails(values).length > 0);
                      } else {
                        setNeedPrincipalEmails(false);
                      }
                      setValues((prev) => ({
                        ...prev,
                        ...nextParent,
                        linked_contract_id: '',
                        linked_contracts: [],
                      }));
                    }}
                  />
                </label>
              </div>
            )}
            {isPrincipalEmailStep && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <p className="wizard-step-prompt" style={{ margin: 0 }}>
                  This form needs an email for every
                  {' '}
                  {(values.represented_sides || []).join(' and ') || 'signer'}
                  {values.agent_is_signer ? ' and listing or buyer agent' : ''}
                  {' '}
                  before it can be generated.
                </p>
                {principalEmailPeople.map((person) => (
                  <label
                    key={`${person.role}-${person.name}`}
                    className="wizard-field"
                    style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                  >
                    <span className="wizard-field-label">
                      Email for
                      {' '}
                      {signerEmailLabel(person)}
                    </span>
                    <input
                      className="wizard-input"
                      type="email"
                      aria-label={`Email for ${signerEmailLabel(person)}`}
                      value={person.email || ''}
                      onChange={(e) => {
                        setValues((prev) => ({
                          ...prev,
                          parties: updatePartyEmail(
                            prev.parties, person.name, person.role, e.target.value,
                          ),
                        }));
                      }}
                      style={{ width: '100%' }}
                    />
                  </label>
                ))}
              </div>
            )}
            {!isParentStep && !isPrincipalEmailStep && (singleChoiceField || isAddressStep || promptOnlyInput) && (
              <div className="wizard-question">
                <h3>{isAddressStep ? PROPERTY_ADDRESS_QUESTION : questionLabel}</h3>
              </div>
            )}
            {!isParentStep && !isPrincipalEmailStep && !singleChoiceField && !isAddressStep && !promptOnlyInput && current.prompt && (
              <p className="wizard-step-prompt">{current.prompt}</p>
            )}
            {allowEarlySubmitAfter && current.step_id === allowEarlySubmitAfter && (
              <p className="wizard-step-prompt">
                Year built is required. Generate a starter PDF if you will finish the
                questions with the seller, or continue to complete every required
                question before e-sign.
              </p>
            )}
            {isAddressStep && (
              <PropertyAddressFields values={values} onChange={setAddressField} />
            )}
            {!isParentStep && !isAddressStep && visibleFields.map((field) => {
              const kind = inputForField(field, current);
              if (kind === 'radio') {
                const groupLabel = titleCaseFieldLabel(labelForField(field, current, labels));
                return (
                  <div
                    key={field}
                    className="wizard-creating-agent-side"
                    role="radiogroup"
                    aria-label={groupLabel}
                  >
                    {!singleChoiceField && (
                      <span className="wizard-field-label">
                        {groupLabel}
                      </span>
                    )}
                    {optionsForField(field, current).map((option) => {
                      const selected = String(values[field] ?? '') === String(option.value);
                      return (
                        <button
                          key={`${field}:${option.value}`}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          className={`wizard-button ${selected ? 'active' : ''}`}
                          onClick={() => (
                            singleChoiceField
                              ? chooseAndAdvance(field, option.value)
                              : setField(field, option.value)
                          )}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                );
              }
              if (kind === 'checkbox') {
                const checked = Boolean(values[field]);
                if (singleChoiceField) {
                  return (
                    <div
                      key={field}
                      className="wizard-yesno"
                      role="radiogroup"
                      aria-label={questionLabel || titleCaseFieldLabel(labelForField(field, current, labels))}
                    >
                      <button
                        type="button"
                        role="radio"
                        aria-checked={checked}
                        className={`wizard-button ${checked ? 'active' : ''}`}
                        onClick={() => chooseAndAdvance(field, true)}
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={values[field] === false}
                        className={`wizard-button ${values[field] === false ? 'active' : ''}`}
                        onClick={() => chooseAndAdvance(field, false)}
                      >
                        No
                      </button>
                    </div>
                  );
                }
                return (
                  <button
                    key={field}
                    type="button"
                    aria-pressed={checked}
                    className={`wizard-button ${checked ? 'active' : ''}`}
                    onClick={() => setField(field, !checked)}
                  >
                    {titleCaseFieldLabel(labelForField(field, current, labels))}
                  </button>
                );
              }
              return (
                <label key={field} className="wizard-field">
                  {!promptOnlyInput && (
                    <span className="wizard-field-label">{titleCaseFieldLabel(labelForField(field, current, labels))}</span>
                  )}
                  {field === 'linked_contract_id' ? (
                    <select
                      value={values.linked_contract_id || ''}
                      onChange={(e) => setField('linked_contract_id', e.target.value)}
                      style={{ width: '100%' }}
                    >
                      <option value="">Select a purchase agreement</option>
                      {linkedContracts.map((row) => (
                        <option key={row.id} value={row.id}>{row.label}</option>
                      ))}
                    </select>
                  ) : kind === 'textarea' ? (
                    <textarea
                      className="wizard-textarea"
                      rows={6}
                      value={values[field] || ''}
                      onChange={(e) => setField(field, e.target.value)}
                      placeholder={placeholderForField(field, current, values, optionalFields)}
                      aria-label={promptOnlyInput ? questionLabel : undefined}
                      style={{ width: '100%' }}
                    />
                  ) : (
                    <input
                      className="wizard-input"
                      type={inputTypeForField(field, current)}
                      value={values[field] || ''}
                      onChange={(e) => setField(field, e.target.value)}
                      placeholder={placeholderForField(field, current, values, optionalFields)}
                      aria-label={promptOnlyInput ? questionLabel : undefined}
                      style={{ width: '100%' }}
                    />
                  )}
                </label>
              );
            })}
            {isPrincipalEmailStep && <WizardContinueHint reason={continueBlockedReason()} />}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
              <button
                type="button"
                className="wizard-button"
                disabled={step === 0 && !onBackToCatalog}
                onClick={() => {
                  if (step === 0 && onBackToCatalog) {
                    onBackToCatalog();
                    return;
                  }
                  if (!goToVisibleStep(step, values, -1)) {
                    const next = Math.max(0, step - 1);
                    stepIdRef.current = steps[next]?.step_id || null;
                    setStep(next);
                  }
                }}
              >
                Back
              </button>
              <div style={{ display: 'flex', gap: 12 }}>
                {allowEarlySubmitAfter && current.step_id === allowEarlySubmitAfter && !last && (
                  <button
                    type="button"
                    className={WIZARD_PRIMARY_BUTTON_CLASS}
                    disabled={!canContinue() || submitting}
                    onClick={() => submit('header')}
                  >
                    {submitting ? 'Generating…' : 'Generate starter PDF'}
                  </button>
                )}
                {showGenerate ? (
                  <button
                    className={WIZARD_PRIMARY_BUTTON_CLASS}
                    type="button"
                    disabled={!canContinue() || submitting || committingParent}
                    onClick={() => (isParentStep ? commitParentThen('generate') : submit('full'))}
                  >
                    {submitting || committingParent ? 'Generating…' : 'Generate form'}
                  </button>
                ) : (
                  <button
                    className={WIZARD_PRIMARY_BUTTON_CLASS}
                    type="button"
                    disabled={!canContinue() || committingParent}
                    onClick={() => {
                      if (isParentStep) {
                        commitParentThen('advance');
                        return;
                      }
                      if (incompleteRequired >= 0 && incompleteRequired < step) {
                        stepIdRef.current = steps[incompleteRequired]?.step_id || null;
                        setStep(incompleteRequired);
                        return;
                      }
                      if (!goToVisibleStep(step, values, 1)) {
                        const next = step + 1;
                        stepIdRef.current = steps[next]?.step_id || null;
                        setStep(next);
                      }
                    }}
                  >
                    Continue
                  </button>
                )}
              </div>
            </div>
          </>
          </WizardStepReady>
        )}
      </div>
    </div>
  );

  if (mode === 'page') return <div className="form-magic-page">{body}</div>;
  return (
    <div className="wizard-overlay" onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(done); }}>
      {body}
    </div>
  );
};

export default ChildFormWizard;
